import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import './App.css';

import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Simulation from './components/Simulation';
import Results from './components/Results';
import AdminPanel from './components/AdminPanel';
import Header from './components/Header';
import AuthCallback from './components/AuthCallback';
import CartridgeForm from './components/CartridgeForm';
import Library from './components/Library';

interface User {
  id: string;
  email: string;
  role: string;
}

interface AppState {
  user: User | null;
  loading: boolean;
  passwordRecovery: boolean;
}

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || '',
  process.env.REACT_APP_SUPABASE_ANON_KEY || ''
);

// Resolve role: participants table > user_metadata > default
async function resolveUser(authUser: any): Promise<User> {
  try {
    const queryPromise = supabase
      .from('participants')
      .select('role')
      .eq('user_id', authUser.id)
      .maybeSingle();

    const timeoutPromise = new Promise<{ data: null; error: null }>((resolve) =>
      setTimeout(() => resolve({ data: null, error: null }), 4000)
    );

    const { data: participant } = await Promise.race([queryPromise, timeoutPromise]);

    return {
      id: authUser.id,
      email: authUser.email || '',
      role: participant?.role || authUser.user_metadata?.role || 'participant',
    };
  } catch {
    return {
      id: authUser.id,
      email: authUser.email || '',
      role: authUser.user_metadata?.role || 'participant',
    };
  }
}

function App() {
  const [state, setState] = useState<AppState>({
    user: null,
    loading: true,
    passwordRecovery: false,
  });

  useEffect(() => {
    // Hydrate session on mount
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const user = await resolveUser(session.user);
        setState({ user, loading: false, passwordRecovery: false });
      } else {
        setState({ user: null, loading: false, passwordRecovery: false });
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          setState(s => ({ ...s, loading: false, passwordRecovery: true, user: null }));
          return;
        }

        if (session?.user) {
          const user = await resolveUser(session.user);
          setState({ user, loading: false, passwordRecovery: false });
        } else {
          setState({ user: null, loading: false, passwordRecovery: false });
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const handleLogin = (user: User) => {
    setState(s => ({ ...s, user, passwordRecovery: false }));
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setState({ user: null, loading: false, passwordRecovery: false });
  };

  if (state.loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        {state.user && <Header user={state.user} onLogout={handleLogout} />}

        <Routes>
          {/* OAuth / email link callback */}
          <Route path="/auth/callback" element={<AuthCallback />} />

          <Route
            path="/login"
            element={
              state.user
                ? <Navigate to="/dashboard" />
                : <Login onLogin={handleLogin} forceResetMode={state.passwordRecovery} />
            }
          />
          <Route
            path="/dashboard"
            element={state.user ? <Dashboard user={state.user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/simulation/:id"
            element={state.user ? <Simulation user={state.user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/results/:id"
            element={state.user ? <Results user={state.user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/cartridge"
            element={state.user ? <CartridgeForm user={state.user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/library"
            element={state.user ? <Library /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin"
            element={
              state.user && state.user.role === 'admin'
                ? <AdminPanel user={state.user} />
                : <Navigate to="/dashboard" />
            }
          />
          <Route path="/" element={<Navigate to={state.user ? '/dashboard' : '/login'} />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
